import React from "react";
import { motion } from "framer-motion";

const HERO_IMG = "/hero-chillblox.png";

const ease = [0.16, 1, 0.3, 1];

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-[100vh] w-full overflow-hidden"
        >
            {/* Background image */}
            <div className="absolute inset-0">
                <img
                    src={HERO_IMG}
                    alt="A hand-carved Chill Blox cube of ice resting inside a tumbler of aged whiskey"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-obsidian-900/85 via-obsidian-900/40 to-obsidian-900" />
                <div className="absolute inset-0 bg-gradient-to-r from-obsidian-900/70 via-transparent to-transparent" />
            </div>

            {/* Content */}
            <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pt-48 md:pt-56 pb-24 min-h-[100vh] flex flex-col justify-between">
                <div className="grid grid-cols-12 gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease, delay: 0.4 }}
                        className="col-span-12 lg:col-span-9"
                    >
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-10 h-px bg-amber-glow" />
                            <span className="overline">Est. MMXXIV · Hand-Carved</span>
                        </div>

                        <h1
                            data-testid="hero-headline"
                            className="font-serif-display text-[13vw] md:text-[9vw] lg:text-[7.2vw] leading-[0.95] tracking-tighter"
                        >
                            The art of <br />
                            the <span className="italic text-amber-glow">perfect</span> pour.
                        </h1>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease, delay: 0.9 }}
                        className="col-span-12 lg:col-span-5 lg:col-start-8 mt-8 lg:mt-16"
                    >
                        <p className="text-ice-200/80 text-base md:text-lg leading-relaxed max-w-md">
                            Gourmet ice — still-cut from filtered spring water, crafted over 72 hours, and
                            carved by hand into sculptural clarity. A quiet luxury for your finest spirit.
                        </p>

                        <div className="mt-10 flex flex-wrap gap-4">
                            <a href="#concierge" data-testid="hero-cta-primary" className="lux-btn lux-btn-primary">
                                Request a Concierge
                                <span aria-hidden>→</span>
                            </a>
                            <a href="#craft" data-testid="hero-cta-secondary" className="lux-btn lux-btn-ghost">
                                Our Craft
                            </a>
                        </div>
                    </motion.div>
                </div>

                {/* Bottom meta row */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.2, ease, delay: 1.4 }}
                    className="mt-24 lg:mt-0 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/10 pt-8"
                >
                    {[
                        { k: "72h", v: "Directional Freeze" },
                        { k: "0", v: "Impurities" },
                        { k: "−14°", v: "Served Cold" },
                        { k: "24h", v: "Concierge Reply" },
                    ].map((stat, i) => (
                        <div key={i} className="flex flex-col gap-1" data-testid={`hero-stat-${i}`}>
                            <span className="font-serif-display text-3xl lg:text-4xl text-ice-50">
                                {stat.k}
                            </span>
                            <span className="text-[0.68rem] uppercase tracking-[0.28em] text-ice-400">
                                {stat.v}
                            </span>
                        </div>
                    ))}
                </motion.div>
            </div>

            {/* Scroll hint */}
            <div className="absolute bottom-8 right-6 md:right-10 lg:right-16 z-10 hidden md:flex items-center gap-3">
                <span className="text-[0.68rem] uppercase tracking-[0.3em] text-ice-400">Scroll</span>
                <div className="w-10 h-px bg-ice-400" />
            </div>
        </section>
    );
}
